/**
 * Timesheet Scanner — upload, scan, confirm, and submit handwritten timesheets.
 *
 * Thin orchestrator. Implementation lives in pages/timesheet-scanner/:
 *   - useTimesheetScanner — fetch + upload + state machine
 *   - UploadDropzone — drag/drop file input
 *   - BulkActionsBar — Auto-Confirm + Review Mode bucket counts
 *   - TimesheetCard — per-row matching, visits, signatures, footer actions
 *   - ScanThumbnail — PDF/image preview + lightbox
 *   - constants — STATUS_MAP, time/unit math, helper classes
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  CaretDown,
  FileText,
  ShieldCheck,
  Spinner,
  Warning,
} from '@phosphor-icons/react';
import { ScannerMetricsWidget } from '../components/timesheet/ScannerMetricsWidget';
import { ReviewMode } from '../components/timesheet/ReviewMode';
import { EVVCrossCheckModal } from '../components/timesheet/EVVCrossCheckModal';
import UploadDropzone from './timesheet-scanner/UploadDropzone';
import BatchProgressPanel from './timesheet-scanner/BatchProgressPanel';
import BulkActionsBar from './timesheet-scanner/BulkActionsBar';
import TimesheetCard from './timesheet-scanner/TimesheetCard';
import { useTimesheetScanner } from './timesheet-scanner/useTimesheetScanner';
import { FILTER_TABS } from './timesheet-scanner/constants';
import { useAgency } from '../context/AgencyContext';
import { openElevationDialog } from '../components/PlatformElevation';

const ElevationRequiredBanner = ({ agency }) => (
  <div
    className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3"
    data-testid="timesheets-elevation-required-banner"
  >
    <ShieldCheck size={24} className="text-indigo-700 shrink-0 mt-0.5" weight="duotone" />
    <div className="flex-1">
      <h3 className="text-sm font-semibold text-indigo-900">
        Platform elevation required
      </h3>
      <p className="text-xs text-indigo-700 mt-0.5">
        You're signed in as AZAI platform staff. To view timesheets for{' '}
        <strong>{agency?.name || 'the selected agency'}</strong>, request a
        time-limited platform elevation. Every access is audited and expires
        automatically.
      </p>
    </div>
    <Button
      onClick={() => agency?.id && openElevationDialog(agency.id, agency.name)}
      className="bg-indigo-900 hover:bg-indigo-800 shrink-0"
      data-testid="timesheets-request-elevation-btn"
    >
      Request access
    </Button>
  </div>
);

const FilterTabs = ({ filterStatus, setFilterStatus, setVisibleCount, counts }) => (
  <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
    {FILTER_TABS.map((t) => (
      <button
        key={t.k}
        onClick={() => {
          setFilterStatus(t.k);
          setVisibleCount(10);
        }}
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
          filterStatus === t.k
            ? 'border-indigo-700 text-indigo-900'
            : 'border-transparent text-slate-500 hover:text-slate-900'
        }`}
        data-testid={`filter-${t.k}`}
      >
        {t.label}
        <Badge className="ml-2 bg-slate-100 text-slate-600 text-[10px] h-4 px-1.5">
          {counts[t.k] ?? 0}
        </Badge>
      </button>
    ))}
  </div>
);

const EmptyState = () => (
  <div
    className="bg-white border border-slate-200 rounded-xl p-16 text-center"
    data-testid="empty-state"
  >
    <FileText size={48} className="mx-auto text-slate-300 mb-4" />
    <h3 className="text-lg font-medium text-slate-700">No timesheets yet</h3>
    <p className="text-sm text-slate-500 mt-1">
      Drop files above to get started. AI will extract every page automatically.
    </p>
  </div>
);

export default function TimesheetScannerPage() {
  const navigate = useNavigate();
  const { currentAgency } = useAgency();
  const {
    timesheets,
    patients,
    employees,
    loading,
    uploading,
    uploadStatus,
    filterStatus,
    setFilterStatus,
    busyId,
    visibleCount,
    setVisibleCount,
    autoConfirming,
    crossCheckTimesheetId,
    setCrossCheckTimesheetId,
    reviewQueue,
    setReviewQueue,
    counts,
    readinessBuckets,
    needsElevation,
    activeBatches,
    dismissBatch,
    fetchTimesheets,
    handleAutoConfirm,
    handleEnterReviewMode,
    handleFiles,
    handleInlineSave,
    handleConfirm,
    handleDelete,
    handleSubmitEvv,
    handleCreateClaims,
  } = useTimesheetScanner();

  const visibleTimesheets = timesheets.slice(0, visibleCount);
  const hasMore = timesheets.length > visibleCount;

  return (
    <div className="space-y-6" data-testid="timesheet-scanner-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Timesheet Scanner</h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload, scan, and confirm handwritten timesheets — all on one page
          </p>
        </div>
      </div>

      {needsElevation && <ElevationRequiredBanner agency={currentAgency} />}

      {!needsElevation && (
        <>
          <ScannerMetricsWidget refreshKey={timesheets.length} />

          <UploadDropzone
            uploading={uploading}
            uploadStatus={uploadStatus}
            onFiles={handleFiles}
          />

          <BatchProgressPanel batches={activeBatches} onDismiss={dismissBatch} />

          <BulkActionsBar
            readinessBuckets={readinessBuckets}
            autoConfirming={autoConfirming}
            onEnterReviewMode={handleEnterReviewMode}
            onAutoConfirm={handleAutoConfirm}
          />

          <FilterTabs
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            setVisibleCount={setVisibleCount}
            counts={counts}
          />
        </>
      )}

      {!needsElevation && (loading ? (
        <div className="flex justify-center py-16">
          <Spinner size={32} className="animate-spin text-indigo-600" />
        </div>
      ) : timesheets.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div
            className="grid grid-cols-1 xl:grid-cols-2 gap-4"
            data-testid="timesheet-cards"
          >
            {visibleTimesheets.map((ts) => (
              <TimesheetCard
                key={ts.id}
                ts={ts}
                patients={patients}
                employees={employees}
                onInlineSave={handleInlineSave}
                onConfirm={handleConfirm}
                onDelete={handleDelete}
                onSubmitEvv={handleSubmitEvv}
                onCreateClaims={handleCreateClaims}
                onViewClaims={() => navigate('/claims')}
                busy={busyId === ts.id}
              />
            ))}
          </div>
          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => setVisibleCount((c) => c + 10)}
                data-testid="load-more-btn"
              >
                <CaretDown size={14} className="mr-1" />
                Load {Math.min(10, timesheets.length - visibleCount)} more (
                {timesheets.length - visibleCount} remaining)
              </Button>
            </div>
          )}
          <div className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
            <Warning size={12} />
            Showing {visibleTimesheets.length} of {timesheets.length} timesheet
            {timesheets.length === 1 ? '' : 's'}
          </div>
        </>
      ))}

      {reviewQueue && (
        <ReviewMode
          queue={reviewQueue}
          patients={patients}
          employees={employees}
          onClose={() => setReviewQueue(null)}
          onRefresh={fetchTimesheets}
        />
      )}

      <EVVCrossCheckModal
        open={!!crossCheckTimesheetId}
        timesheetId={crossCheckTimesheetId}
        onClose={() => setCrossCheckTimesheetId(null)}
        onSubmitted={() => {
          fetchTimesheets();
          setCrossCheckTimesheetId(null);
        }}
      />
    </div>
  );
}
